import React, { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react';

const COLLAPSED_KEY = 'atlas-sidebar-collapsed';

interface SidebarContextType {
  isCollapsed: boolean;
  toggleCollapsed: () => void;
  sidebarWidth: number;
  mobileTitleOverride: string | null;
  setMobileTitleOverride: (title: string | null) => void;
  mobileBackOverride: (() => void) | null;
  setMobileBackOverride: (fn: (() => void) | null) => void;
  mobileHideBack: boolean;
  setMobileHideBack: (hide: boolean) => void;
  /** Si true, el header mobile pierde su `bg-white` + border y los
   *  botones (back / menu) toman fondo blanco circular semi-opaco.
   *  Pensado para pantallas como el preview de plantillas donde la
   *  imagen ocupa todo y el header debe flotar encima. */
  mobileHeaderTransparent: boolean;
  setMobileHeaderTransparent: (v: boolean) => void;
  /** Reemplaza el título centrado del header mobile por un ReactNode
   *  arbitrario (ej. un toggle, chips). Si es null, se muestra el
   *  título habitual. */
  mobileHeaderCenter: ReactNode | null;
  setMobileHeaderCenter: (node: ReactNode | null) => void;
  /** Oculta el pill de créditos en el header mobile transparente.
   *  Útil cuando la pantalla ya muestra otros CTAs o cuando el
   *  contador no aporta valor (ej. resultados de catálogo). */
  mobileHideCreditsPill: boolean;
  setMobileHideCreditsPill: (v: boolean) => void;
}

const SidebarContext = createContext<SidebarContextType>({
  isCollapsed: false,
  toggleCollapsed: () => {},
  sidebarWidth: 240,
  mobileTitleOverride: null,
  setMobileTitleOverride: () => {},
  mobileBackOverride: null,
  setMobileBackOverride: () => {},
  mobileHideBack: false,
  setMobileHideBack: () => {},
  mobileHeaderTransparent: false,
  setMobileHeaderTransparent: () => {},
  mobileHeaderCenter: null,
  setMobileHeaderCenter: () => {},
  mobileHideCreditsPill: false,
  setMobileHideCreditsPill: () => {},
});

export const useSidebar = () => useContext(SidebarContext);

export const SidebarProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isCollapsed, setIsCollapsed] = useState(() => {
    try { return localStorage.getItem(COLLAPSED_KEY) === 'true'; } catch { return false; }
  });

  const toggleCollapsed = useCallback(() => {
    setIsCollapsed(prev => {
      const next = !prev;
      try { localStorage.setItem(COLLAPSED_KEY, String(next)); } catch {}
      return next;
    });
  }, []);

  // Listen for sidebar state changes via custom events (from Sidebar component)
  useEffect(() => {
    const handler = (e: CustomEvent) => {
      setIsCollapsed(e.detail.isCollapsed);
      try { localStorage.setItem(COLLAPSED_KEY, String(e.detail.isCollapsed)); } catch {}
    };
    window.addEventListener('sidebar-toggle' as any, handler as EventListener);
    return () => window.removeEventListener('sidebar-toggle' as any, handler as EventListener);
  }, []);

  const [mobileTitleOverride, setMobileTitleOverride] = useState<string | null>(null);
  const [mobileBackOverride, setMobileBackOverride] = useState<(() => void) | null>(null);
  const [mobileHideBack, setMobileHideBack] = useState(false);
  const [mobileHeaderTransparent, setMobileHeaderTransparent] = useState(false);
  const [mobileHeaderCenter, setMobileHeaderCenter] = useState<ReactNode | null>(null);
  const [mobileHideCreditsPill, setMobileHideCreditsPill] = useState(false);

  // Sidebar visual width + 12px left offset + 12px gap to content
  const sidebarWidth = (isCollapsed ? 72 : 240) + 24;

  return (
    <SidebarContext.Provider value={{ isCollapsed, toggleCollapsed, sidebarWidth, mobileTitleOverride, setMobileTitleOverride, mobileBackOverride, setMobileBackOverride, mobileHideBack, setMobileHideBack, mobileHeaderTransparent, setMobileHeaderTransparent, mobileHeaderCenter, setMobileHeaderCenter, mobileHideCreditsPill, setMobileHideCreditsPill }}>
      {children}
    </SidebarContext.Provider>
  );
};
